<template>
    <slot v-bind="{
        ...state,
        setForm,
    } "/>
</template>

<script setup>
import { onMounted, reactive } from 'vue';
import { loadRecaptcha } from '../utils/recaptcha';

const props = defineProps({
    gtmEvent: {
        type: String,
        default: null,
    },
    submittingMessage: {
        type: String,
        default: 'Signing you up…',
    },
    successMessage: {
        type: String,
        default: 'Thank you for signing up for the LA Phil email list.',
    },
    newUserMessage: {
        type: String,
        default: 'Thank you for joining our mailing list!',
    },
    existingUserMessage: {
        type: String,
        default: 'Thank you, you are already signed up.',
    },
    errorMessage: {
        type: String,
        default: 'Sorry an error occurred, please try again!',
    },
});

const state = reactive({
    isProcessing: false,
    message: null,
});

let form;
let recaptchaWidgetId = null;
let recaptchaResolve = null;

const setForm = element => {
    form = element;
}

function renderRecaptchaWidget() {
    if (!form || recaptchaWidgetId !== null) {
        return;
    }

    let container = form.querySelector('.g-recaptcha');

    if (!container) {
        container = document.createElement('div');
        container.className = 'g-recaptcha';
        form.appendChild(container);
    }

    recaptchaWidgetId = window.grecaptcha.render(container, {
        sitekey: window.AppConfig.recaptcha,
        size: 'invisible',
        callback: (token) => {
            if (recaptchaResolve) {
                recaptchaResolve(token);
                recaptchaResolve = null;
            }
        },
    });
}

function executeRecaptcha() {
    return new Promise((resolve) => {
        recaptchaResolve = resolve;
        window.grecaptcha.execute(recaptchaWidgetId);
    });
}

function submitForm(event, recaptchaToken) {
    state.isProcessing = true;
    state.message = props.submittingMessage;

    fetch(event.target.action, { // eslint-disable-line
        method: 'POST',
        headers: {
            'Content-type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
            email: event.target.elements['email']?.value.trim(),
            customer_contact_point_purposes: event.target.elements['customer_contact_point_purposes']?.value.trim() ?? null,
            ...(recaptchaToken ? { 'g-recaptcha-response': recaptchaToken } : {}),
        }),
    }).then(response => {
        return response.json();
    }).then(response => {
        if (response.data.error) {
            state.message = response.data.description;
        } else if (response.data && response.data.new_user) {
            state.message = props.newUserMessage;

            if (props.gtmEvent && 'dataLayer' in window) {
                window.dataLayer.push({
                    event: props.gtmEvent,
                });
            }
        } else {
            state.message = props.existingUserMessage;
        }
    }).catch(error => {
        console.error('error submitting subscription', error);

        if (error.data && error.data.description) {
            state.message = error.data.description;
        } else {
            state.message = props.errorMessage;
        }
    }).finally(() => {
        state.isProcessing = false;

        if (recaptchaWidgetId !== null) {
            window.grecaptcha.reset(recaptchaWidgetId);
        }
    });
}

async function setupRecaptcha() {
    await loadRecaptcha();
    renderRecaptchaWidget();
}

onMounted(() => {
    form?.addEventListener('submit', async (event) => {
        event.preventDefault();

        let recaptchaToken = null;

        if (window.AppConfig.recaptcha) {
            await setupRecaptcha();
            recaptchaToken = await executeRecaptcha();
        }

        submitForm(event, recaptchaToken);
    });

    if (window.AppConfig.recaptcha) {
        setupRecaptcha();
    }
});
</script>
